import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import './listaTexto.css';

/**
 * Conjunto editável de textos curtos.
 *
 * O `<input>` interno NÃO tem `name`: quem carrega o valor para o formulário é
 * o estado do React, lido pelo componente pai. Um input com nome enviaria o
 * texto ainda não confirmado junto com a lista, e o servidor receberia um item
 * a mais que o usuário não adicionou.
 *
 * Backspace no campo vazio remove o último item — é o gesto que quem usa
 * campos de etiqueta espera, e evita ter que mirar num alvo de 24px.
 */
export function ListaTexto({
  label, valores, onChange, hint, placeholder, max = 40,
}: {
  label: string;
  valores: string[];
  onChange: (valores: string[]) => void;
  hint?: string;
  placeholder?: string;
  max?: number;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const [rascunho, setRascunho] = useState('');
  const entrada = useRef<HTMLInputElement>(null);

  function adicionar(bruto: string) {
    const item = bruto.trim();
    // Duplicata é descartada em silêncio: o servidor também deduplica, e um
    // erro para "você já adicionou isso" seria ruído sobre algo inofensivo.
    if (!item || valores.includes(item) || valores.length >= max) return;
    onChange([...valores, item]);
    setRascunho('');
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'Enter' || evento.key === ',') {
      // Enter aqui NÃO pode enviar o formulário: a intenção é adicionar item.
      evento.preventDefault();
      adicionar(rascunho);
    } else if (evento.key === 'Backspace' && rascunho === '' && valores.length) {
      onChange(valores.slice(0, -1));
    }
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      <div className="lista-texto">
        {valores.map((item) => (
          <span className="lista-texto__item" key={item}>
            {item}
            <button
              type="button"
              className="lista-texto__remover"
              aria-label={`Remover ${item}`}
              onClick={() => { onChange(valores.filter((v) => v !== item)); entrada.current?.focus(); }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={entrada}
          id={id}
          className="lista-texto__entrada"
          value={rascunho}
          placeholder={valores.length ? undefined : placeholder}
          aria-describedby={hintId}
          onChange={(e) => setRascunho(e.currentTarget.value)}
          onKeyDown={aoTeclar}
          /* Sair do campo confirma o que estava digitado. Sem isto, quem
             preenche e clica direto em "Salvar" perde o último item. */
          onBlur={() => adicionar(rascunho)}
        />
      </div>
      {hint && <p className="field__hint" id={hintId}>{hint}</p>}
      {/* A contagem só aparece perto do limite: mostrar "3 de 40" o tempo todo
          é ruído, mostrar "38 de 40" é aviso. */}
      {valores.length > max - 5 && (
        <p className="field__hint">{`${valores.length} de ${max}`}</p>
      )}
    </div>
  );
}
